/**
 * StaffPortalLeaveDocumentService
 * Service for staff leave document operations in the portal.
 * Route: api/staff-portal/leaves/{leaveId}/documents
 */
import { StaffPortalBaseService } from './StaffPortalBaseService';
/**
 * StaffPortalLeaveDocumentService
 * Handles leave document operations for the staff portal.
 * Maps to /api/staff-portal/leaves/{leaveId}/documents endpoint
 */
export class StaffPortalLeaveDocumentService extends StaffPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? StaffPortalLeaveDocumentService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? StaffPortalLeaveDocumentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Build URL for leave document operations
     */
    buildDocumentUrl(leaveId, documentId) {
        const base = this.buildUrl(`${leaveId}/documents`);
        return documentId ? `${base}/${documentId}` : base;
    }
    // ==================== LIST OPERATIONS ====================
    /**
     * Get all documents for a leave request
     * GET /api/staff-portal/leaves/{leaveId}/documents
     */
    async getDocuments(leaveId) {
        const response = await this.getDocumentsRaw(leaveId);
        return response.data ?? [];
    }
    /**
     * Get documents (raw response)
     */
    getDocumentsRaw(leaveId) {
        const url = this.buildDocumentUrl(leaveId);
        return this.http.get(url);
    }
    // ==================== GET SINGLE DOCUMENT ====================
    /**
     * Get a specific document by ID
     * GET /api/staff-portal/leaves/{leaveId}/documents/{documentId}
     */
    async getDocument(leaveId, documentId) {
        const response = await this.getDocumentRaw(leaveId, documentId);
        if (!response.data) {
            throw new Error(`Document ${documentId} not found`);
        }
        return response.data;
    }
    /**
     * Get document (raw response)
     */
    getDocumentRaw(leaveId, documentId) {
        const url = this.buildDocumentUrl(leaveId, documentId);
        return this.http.get(url);
    }
    // ==================== CREATE OPERATIONS ====================
    /**
     * Upload a document to a leave request
     * POST /api/staff-portal/leaves/{leaveId}/documents
     */
    async createDocument(leaveId, data) {
        const response = await this.createDocumentRaw(leaveId, data);
        if (!response.data) {
            throw new Error('Failed to upload document');
        }
        return response.data;
    }
    /**
     * Create document (raw response)
     */
    createDocumentRaw(leaveId, data) {
        const url = this.buildDocumentUrl(leaveId);
        return this.http.post(url, data);
    }
    // ==================== DELETE OPERATIONS ====================
    /**
     * Delete a document
     * DELETE /api/staff-portal/leaves/{leaveId}/documents/{documentId}
     */
    async deleteDocument(leaveId, documentId) {
        const response = await this.deleteDocumentRaw(leaveId, documentId);
        if (!response.success) {
            throw new Error('Failed to delete document');
        }
    }
    /**
     * Delete document (raw response)
     */
    deleteDocumentRaw(leaveId, documentId) {
        const url = this.buildDocumentUrl(leaveId, documentId);
        return this.http.delete(url);
    }
}
StaffPortalLeaveDocumentService.DEFAULT_SERVICE_PATH = 'hr';
StaffPortalLeaveDocumentService.BASE_PATH = 'api/staff-portal/leaves';
//# sourceMappingURL=StaffPortalLeaveDocumentService.js.map